import { Box, Divider, Menu, MenuItem } from "@mui/material";
import { useCallback, useState } from "react";
import AddCommentCard from "../comments/AddCommentCard";
import { ISheetComment } from "../comments/CommentContainer";
import { AccountTree, AddCommentOutlined, Description, Image, MenuBook, ContentCopy, ContentPaste } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import AddRemoveNodeTagMenuItem from "../tags/AddRemoveNodeTagMenuItem";
import AddTagDialog from "../tags/AddTagDialog";
import { updateFocus, updateMode } from "../../redux/slices/editor";
import { connect } from "react-redux";
import { Cursor } from "../../fstn/cursor";
import { GridCellParams } from "@mui/x-data-grid-pro";
import { GridApiPro } from "@mui/x-data-grid-pro/models/gridApiPro";
import {RemoveNodeTagTypeMenuItem} from "../tags/RemoveNodeTagTypeMenuItem";

const useStyles = makeStyles((theme) => ({
    icon: {
        paddingRight: theme.spacing(1)
    },
    commentsIcon: {
        color: theme.palette.cellComment.main,
        paddingRight: theme.spacing(1)
    }
}));


export interface Cell
{
    column: string
    row: string
    stateId?: string
}

interface Props
{
    cursor: Cursor
    selection: string[]
    target: HTMLElement | null;
    sheetId: string
    cell: Cell
    gridApi: GridApiPro
    onClose: () => void
    updateMode: (mode: string) => void
    updateFocus: (focus: string) => void
    onCopy: (rowId: string, field: string) => void
    onPaste: (rowId: string, field: string) => void
}
function SheetContextMenu({ cursor, gridApi, sheetId, cell, target, selection, onClose, updateMode, updateFocus, onCopy, onPaste }: Props)
{
    const { column, row, stateId } = cell;
    const [addCommentTarget, setAddCommentTarget] = useState<HTMLElement | null>(null);
    const [addTagType, setAddTagType] = useState("");
    const classes = useStyles();

    const getSelectionDetails = (): ISheetComment =>
    {
        return {
            name: "sheet",
            sheetId,
            column,
            row,
        }
    };

    const handleAddTagClose = () => {
        setAddTagType("");
        onClose();
    }
    const handleClickGotoGraph = useCallback(() =>
    {
        if (stateId)
        {
            updateMode("graph");
            updateFocus(stateId);
        }
    }, [stateId, updateFocus, updateMode])

    const handleClickGotoWriter = useCallback(() =>
    {
        if (stateId)
        {
            updateMode("writer");
            updateFocus(stateId);
        }
    }, [stateId, updateFocus, updateMode]);

    const handleClickGotoIntertitle = useCallback(() =>
    {
        if (stateId)
        {
            updateMode("intertitle");
            updateFocus(stateId);
            cursor.forceState(stateId);
        }
    }, [stateId, updateFocus, updateMode, cursor]);

    const handleClickGotoScript = useCallback(() =>
    {
        if (stateId)
        {
            updateMode("script");
            updateFocus(stateId);
        }
    }, [stateId, updateFocus, updateMode])

    const open = Boolean(target);

    const stateInMultiSelect = selection.length > 1 && selection.some((entry) => entry === stateId);

    const selectionStates = stateInMultiSelect ? selection : [String(stateId)];

    const cellParams: GridCellParams | null = "getCellParams" in gridApi && row && column ? gridApi.getCellParams(row, column) : null;

    return <>
        <Menu open={open} anchorEl={target} onClose={onClose} anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}>
            <MenuItem onClick={() => { onCopy(row, column); onClose(); }}>
                <ContentCopy fontSize="small" className={classes.icon} />Copy
            </MenuItem>
            <MenuItem disabled={!(cellParams?.isEditable ?? false)}
                onClick={() => { onPaste(row, column); onClose(); }}>
                <ContentPaste fontSize="small" className={classes.icon} />Paste
            </MenuItem>
            <Divider />
            {stateId && <Box>
                <AddRemoveNodeTagMenuItem onClose={onClose} parentOpen={open} selection={selectionStates} setAddTagType={setAddTagType} />
                <RemoveNodeTagTypeMenuItem onClose={onClose} parentOpen={open} selection={selectionStates}/>
                <Divider />
                <MenuItem onClick={handleClickGotoGraph}><AccountTree fontSize="small" className={classes.icon} />Goto Graph</MenuItem>
                <MenuItem onClick={handleClickGotoWriter}><Description fontSize="small" className={classes.icon} />Goto Writer</MenuItem>
                <MenuItem onClick={handleClickGotoIntertitle}><Image fontSize="small" className={classes.icon} />Goto Intertitle</MenuItem>
                <MenuItem onClick={handleClickGotoScript}><MenuBook fontSize="small" className={classes.icon} />Goto Script</MenuItem>
                <Divider />
            </Box>}
            <MenuItem onClick={() => { setAddCommentTarget(target); onClose(); }}><AddCommentOutlined fontSize="small" className={classes.commentsIcon} />Add Comment</MenuItem>
        </Menu>
        <AddCommentCard target={addCommentTarget} getSelectionDetails={getSelectionDetails} onClose={() => { setAddCommentTarget(null) }} />
        {addTagType && stateId &&
            <AddTagDialog type={addTagType} selection={selectionStates} onClose={handleAddTagClose} />}
    </>
}

const mapStateToProps = (store: never) => ({
});
export default connect(mapStateToProps, {
    updateFocus,
    updateMode
})(SheetContextMenu);